import {
  ThemeEvents,
  VariantUpdateEvent,
  SellingPlanUpdateEvent,
} from "@theme/events";

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.addEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice,
    );
    closestSection.addEventListener(
      ThemeEvents.sellingPlanUpdate,
      this.updatePrice,
    );
  }

  disconnectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice,
    );
    closestSection.removeEventListener(
      ThemeEvents.sellingPlanUpdate,
      this.updatePrice,
    );
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent | SellingPlanUpdateEvent} event - The update event.
   */
  updatePrice = (event) => {
    const data = event.detail?.data || {};
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId && data.productId !== this.dataset.productId) {
      return;
    }

    const html = data.html;
    if (!html) return;

    // Find the matching product-price in the updated HTML using this element's identifiers.
    // Require product-id; if block-id exists, require it too. No fallback to other prices.
    const selector = this.dataset.blockId
      ? `product-price[data-product-id="${this.dataset.productId}"][data-block-id="${this.dataset.blockId}"]`
      : `product-price[data-product-id="${this.dataset.productId}"]`;
    const newProductPrice = html.querySelector(selector);
    if (!newProductPrice) return;

    // Update price container
    const newPriceTemplate =
      newProductPrice.querySelector('[data-ref="priceContainer"]') ||
      newProductPrice.firstElementChild;
    const newPrice = newPriceTemplate ? newPriceTemplate.cloneNode(true) : null;
    const currentPrice =
      this.querySelector('[data-ref="priceContainer"]') ||
      this.firstElementChild;
    if (!newPrice || !currentPrice) return;
    currentPrice.replaceWith(newPrice);
    // Update volume pricing note
    const currentNote = this.querySelector(".volume-pricing-note");
    const newNoteTemplate = newProductPrice.querySelector(
      ".volume-pricing-note",
    );
    const newNote = newNoteTemplate ? newNoteTemplate.cloneNode(true) : null;

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[data-ref="priceContainer"]')?.insertAdjacentElement(
        "afterend",
        /** @type {Element} */ (newNote.cloneNode(true)),
      );
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get("product-price")) {
  customElements.define("product-price", ProductPrice);
}
